import React, { useLayoutEffect, useRef } from 'react';
import SectionShell from '../SectionShell';
import { useT } from '../../i18n/LanguageContext';

/**
 * Sechs Gründe — self-contained slide with a PowerPoint-style auto-fit
 * scaler: the content is measured after render and, if it exceeds the
 * viewport, uniformly scaled down so ALL six cards are always fully
 * visible — regardless of resolution, zoom or global CSS overrides.
 * Uses div-only markup + hg2-* classes so legacy index.css rules
 * (#hauptgruende h2, .hg-card, …) cannot interfere.
 */
export default function SectionHauptgruende() {
  const { t } = useT();
  const outerRef = useRef(null);
  const innerRef = useRef(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;

    const fit = () => {
      inner.style.transform = 'none';
      const rect = outer.getBoundingClientRect();
      const cs = window.getComputedStyle(outer);
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const availH = Math.min(rect.height, window.innerHeight - Math.max(0, rect.top)) - padY;
      const availW = rect.width - padX;
      const needH = inner.scrollHeight;
      const needW = inner.scrollWidth;
      if (availH <= 0 || !needH) return;
      const s = Math.min(1, availH / needH, availW / needW);
      inner.style.transform = s < 0.999 ? `scale(${s})` : 'none';
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(outer);
    ro.observe(inner);
    window.addEventListener('resize', fit);
    const t1 = setTimeout(fit, 120);
    const t2 = setTimeout(fit, 500);
    const t3 = setTimeout(fit, 1200);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  return (
    <SectionShell id="hauptgruende" className="hg2-section" fullBleed>
      <div className="hg2-outer" ref={outerRef} data-testid="hauptgruende-outer">
        <div className="hg2-inner" ref={innerRef} data-testid="hauptgruende-inner">
          <div className="hg2-header">
            <div className="overline hg2-overline">{t.hauptgruende.overline}</div>
            <div className="font-display hg2-title">{t.hauptgruende.title}</div>
            <div className="font-ui hg2-lead">{t.hauptgruende.lead}</div>
          </div>

          <div className="hg2-grid" data-testid="hauptgruende-grid">
            {t.hauptgruende.items.map((it, i) => (
              <div
                key={it.n}
                data-testid={`reason-${it.n}`}
                className="hg2-card"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="hg2-rank rank-number">{it.n}</div>
                <div className="hg2-img-wrap">
                  <img
                    src={it.img || `/source-pptx/image${22 + i}-cut.png`}
                    alt=""
                    loading="lazy"
                    draggable={false}
                    aria-hidden="true"
                  />
                </div>
                <div className="font-display hg2-card-title">{it.title}</div>
                <div className="font-ui hg2-card-body">{it.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
